/**
 * pushNotificationService.js - Native iOS push notification registration
 *
 * Handles APNs push notification registration for iOS devices:
 * - Registers device with APNs to get device token
 * - Stores device token in Supabase push_subscriptions table
 * - Handles permission requests on iOS
 * - Listens for push notification events
 * - Clears badge count and delivered notifications
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';

const DEBUG = true;

/**
 * @param {...any} args
 */
function log(...args) {
  if (DEBUG) {
    logger.info('[NativePush]', ...args);
  }
}

/**
 * Check if we're running on a native iOS platform
 * @returns {boolean}
 */
export function isNativePlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * Check if we're specifically on iOS
 * @returns {boolean}
 */
export function isIOS() {
  return Capacitor.getPlatform() === 'ios';
}

/**
 * Request permission for push notifications on iOS
 * @returns {Promise<boolean>} - True if permission granted
 */
export async function requestPushPermission() {
  if (!isNativePlatform()) {
    log('Not a native platform, skipping push permission request');
    return false;
  }

  try {
    log('Requesting push notification permission...');
    const result = await PushNotifications.requestPermissions();
    log('Permission result:', result);

    if (result.receive === 'granted') {
      log('Push notification permission granted');
      return true;
    } else {
      log('Push notification permission denied:', result.receive);
      return false;
    }
  } catch (error) {
    log('Error requesting push permission:', error);
    return false;
  }
}

/**
 * Register for push notifications and get device token
 * Must be called after permission is granted
 * @returns {Promise<void>}
 */
export async function registerForPushNotifications() {
  if (!isNativePlatform()) {
    log('Not a native platform, skipping push registration');
    return;
  }

  try {
    log('Registering for push notifications...');
    await PushNotifications.register();
    log('Push registration initiated');
  } catch (error) {
    log('Error registering for push:', error);
    throw error;
  }
}

/**
 * Save iOS device token to Supabase
 * @param {string} userId - The user's ID
 * @param {string} deviceToken - The APNs device token
 * @returns {Promise<boolean>} - True if saved successfully
 */
export async function saveDeviceToken(userId, deviceToken) {
  if (!userId || !deviceToken) {
    log('Missing userId or deviceToken, cannot save');
    return false;
  }

  try {
    log('Saving device token for user:', userId);
    log('Device token:', deviceToken.substring(0, 20) + '...');

    // Remove any existing subscription for this device token to avoid duplicates
    const { error: deleteError } = await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('device_token', deviceToken);

    if (deleteError) {
      log('Warning: Error deleting existing subscription:', deleteError);
    }

    // Insert new subscription with iOS platform
    const { data, error } = await supabase.from('push_subscriptions').insert({
      user_id: userId,
      platform: 'ios',
      device_token: deviceToken,
      endpoint: `apns://${deviceToken}`,
      p256dh: '',
      auth: '',
    });

    if (error) {
      log('Error saving device token:', error);
      return false;
    }

    log('Device token saved successfully');
    return true;
  } catch (error) {
    log('Exception saving device token:', error);
    return false;
  }
}

/**
 * Remove device token from Supabase (for logout/unsubscribe)
 * @param {string} userId - The user's ID
 * @param {string} deviceToken - The APNs device token
 * @returns {Promise<boolean>}
 */
export async function removeDeviceToken(userId, deviceToken) {
  if (!userId || !deviceToken) {
    return false;
  }

  try {
    log('Removing device token for user:', userId);

    const { error } = await supabase
      .from('push_subscriptions')
      .delete()
      .eq('user_id', userId)
      .eq('device_token', deviceToken);

    if (error) {
      log('Error removing device token:', error);
      return false;
    }

    log('Device token removed successfully');
    return true;
  } catch (error) {
    log('Exception removing device token:', error);
    return false;
  }
}

/**
 * Clear the app badge count and remove all delivered notifications from the
 * notification center. Safe to call on any platform — no-ops on web.
 * @returns {Promise<void>}
 */
export async function clearBadgeAndNotifications() {
  if (!isNativePlatform()) return;
  try {
    await PushNotifications.removeAllDeliveredNotifications();
    log('Cleared delivered notifications and badge');
  } catch (error) {
    log('Error clearing badge/notifications:', error);
  }
}

/**
 * Initialize push notification listeners
 * Should be called once at app startup
 * @param {Object} options
 * @param {function(string): void} options.onTokenReceived - Callback when token is received
 * @param {function(any): void} options.onNotificationReceived - Callback when notification received
 * @param {function(any): void} options.onNotificationTapped - Callback when notification tapped
 * @param {function(Error): void} options.onError - Callback on error
 * @returns {Promise<void>}
 */
export async function initializePushListeners({
  onTokenReceived,
  onNotificationReceived,
  onNotificationTapped,
  onError,
}) {
  if (!isNativePlatform()) {
    log('Not a native platform, skipping push listener initialization');
    return;
  }

  log('Initializing push notification listeners...');

  // Registration success - we get the device token here
  await PushNotifications.addListener('registration', (token) => {
    log('Push registration success, token:', token.value?.substring(0, 20) + '...');
    if (onTokenReceived) {
      onTokenReceived(token.value);
    }
  });

  // Registration error
  await PushNotifications.addListener('registrationError', (error) => {
    log('Push registration error:', error);
    if (onError) {
      onError(new Error(error.error || 'Registration failed'));
    }
  });

  // Notification received while app is in foreground
  await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    log('Push notification received:', notification);
    if (onNotificationReceived) {
      onNotificationReceived(notification);
    }
  });

  // Notification tapped (app opened from notification)
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    log('Push notification action performed:', action);
    if (onNotificationTapped) {
      onNotificationTapped(action.notification);
    }
  });

  log('Push notification listeners initialized');
}

/**
 * Full initialization flow for iOS push notifications
 * Call this after user is authenticated
 * @param {string} userId - The authenticated user's ID
 * @returns {Promise<boolean>} - True if successfully registered
 */
export async function initializeIOSPush(userId) {
  if (!isNativePlatform()) {
    log('Not a native platform, skipping iOS push initialization');
    return false;
  }

  if (!isIOS()) {
    log('Not iOS, skipping iOS push initialization');
    return false;
  }

  if (!userId) {
    log('No userId provided, cannot initialize push');
    return false;
  }

  try {
    log('Starting iOS push initialization for user:', userId);

    // Request permission
    const permissionGranted = await requestPushPermission();
    if (!permissionGranted) {
      log('Push permission not granted');
      return false;
    }

    // Set up listeners before registering
    await initializePushListeners({
      onTokenReceived: async (token) => {
        // Save token to Supabase when received
        await saveDeviceToken(userId, token);
      },
      onNotificationReceived: (notification) => {
        log('Foreground notification:', notification.title);
      },
      onNotificationTapped: (notification) => {
        log('Notification tapped:', notification.title);
        // Clear badge immediately when user taps a notification
        clearBadgeAndNotifications();
        const data = notification.data || {};
        const sessionId = data.session_id;

        if (sessionId && typeof window !== 'undefined') {
          log('Deep linking to chat session:', sessionId);

          if (
            window.location.pathname === '/chat' ||
            window.location.pathname.startsWith('/chat')
          ) {
            // Already on chat tab — just select the session
            window.dispatchEvent(
              new CustomEvent('timegenie:chat-select', {
                detail: { sessionId },
              }),
            );
            log('Already on chat, dispatched chat-select for session:', sessionId);
          } else {
            // Navigate to chat with session param — useChatManager reads it on mount
            window.location.href = `/chat?session=${sessionId}`;
            log('Navigating to chat with session:', sessionId);
          }
        } else if (typeof window !== 'undefined') {
          // No session_id — fall back to url or /chat
          const url = data.url || '/chat';
          log('No session_id, navigating to:', url);
          window.location.href = url;
        }
      },
      onError: (error) => {
        log('Push error:', error.message);
      },
    });

    // Register for push notifications (this triggers the token callback)
    await registerForPushNotifications();

    log('iOS push initialization complete');
    return true;
  } catch (error) {
    log('Error initializing iOS push:', error);
    return false;
  }
}
